<template>
  <div>
    <ClinicPageHeader
      title="Reagendamentos"
      subtitle="Fila operacional de pacientes aguardando novo horário"
    />

    <div class="dbody">
      <!-- Erro -->
      <ClinicStateBox
        v-if="state === 'error'"
        state="error"
        title="Não foi possível carregar os reagendamentos"
        message="Ocorreu um erro ao buscar a fila de reagendamentos. Tente novamente."
      >
        <template #actions>
          <button class="btn btn--primary btn--sm" @click="reload">
            Tentar de novo
          </button>
        </template>
      </ClinicStateBox>

      <!-- Loading -->
      <template v-else-if="state === 'loading'">
        <div class="metrics3" style="margin-bottom:var(--space-5)">
          <div v-for="i in 3" :key="i" class="metric">
            <div class="skel-line" style="width:70%;margin-bottom:8px" />
            <div class="skel-line" style="width:45%;height:24px" />
          </div>
        </div>
        <div class="card">
          <div class="skel-line" style="width:100%;height:240px" />
        </div>
      </template>

      <!-- Conteúdo -->
      <template v-else>
        <div class="metrics3" style="margin-bottom:var(--space-5)">
          <div v-for="kpi in kpis" :key="kpi.label" class="metric">
            <span class="metric-label">{{ kpi.label }}</span>
            <span class="metric-value sm" :class="kpi.valueClass">
              {{ kpi.value }}
            </span>
            <span class="metric-foot">{{ kpi.foot }}</span>
          </div>
        </div>

        <ClinicSeg
          v-model="filter"
          :options="filters"
          style="margin-bottom:var(--space-5)"
        />

        <div v-if="filteredQueue.length" class="card card--flush">
          <table class="table">
            <thead>
              <tr>
                <th>Paciente</th>
                <th>Profissional de saúde</th>
                <th>Horário original</th>
                <th>Novo horário</th>
                <th>Motivo</th>
                <th class="t-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in filteredQueue" :key="item.id">
                <td>
                  <div class="row" style="gap:var(--space-3)">
                    <div class="avatar avatar--sm" :class="item.avatarClass">
                      {{ item.initials }}
                    </div>
                    <span class="cell-strong">{{ item.patientName }}</span>
                  </div>
                </td>
                <td>{{ item.doctorName }}</td>
                <td
                  class="num"
                  style="color:var(--color-gray-400);text-decoration:line-through"
                >
                  {{ item.oldSlot }}
                </td>
                <td class="num">
                  <strong style="color:var(--color-gray-900)">
                    {{ item.suggested ? formatSlot(item.suggested) : "—" }}
                  </strong>
                </td>
                <td style="color:var(--color-gray-500)">{{ item.reason }}</td>
                <td class="t-right">
                  <div class="queue-acts">
                    <span
                      v-if="item.status === 'sugerido'"
                      class="badge badge--info"
                    >
                      Sugerido
                    </span>
                    <button
                      v-else-if="canEdit('reagendamentos')"
                      class="btn btn--outline btn--sm"
                      @click="suggest(item)"
                    >
                      Sugerir horário
                    </button>
                    <button
                      v-if="canEdit('reagendamentos')"
                      class="btn btn--primary btn--sm"
                      @click="apply(item)"
                    >
                      Reagendar
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <ClinicStateBox
          v-else
          state="empty"
          title="Nenhum reagendamento na fila"
          message="Não há pacientes aguardando novo horário nesta unidade."
        />
      </template>
    </div>
    <ClinicPickTime
      v-if="picker"
      :doctor-id="picker.item.doctorId"
      :title="
        picker.mode === 'suggest' ? 'Sugerir horário' : 'Reagendar consulta'
      "
      :cta="picker.mode === 'suggest' ? 'Enviar sugestão' : 'Reagendar'"
      :subtitle="`${picker.item.patientName} · ${picker.item.doctorName}`"
      @close="picker = null"
      @pick="onPick"
    />

    <!-- Confirmação antes de efetivar o reagendamento (evita clique por engano) -->
    <ClinicConfirmDialog
      v-if="applyConfirm"
      title="Reagendar consulta"
      confirm-label="Sim, reagendar"
      :loading="applying"
      @confirm="confirmApply"
      @close="applyConfirm = null"
    >
      Tem certeza de que deseja reagendar a consulta de
      {{ applyConfirm.item.patientName }} com
      <strong>{{ applyConfirm.item.doctorName }}</strong>
      para
      <strong>{{ formatSlotLong(applyConfirm.slot) }}</strong>
      ?
    </ClinicConfirmDialog>
  </div>
</template>

<script>
import clinicCan from "@/mixins/clinicCan";
import ClinicPageHeader from "@/components/clinica/ClinicPageHeader.vue";
import ClinicStateBox from "@/components/clinica/ClinicStateBox.vue";
import ClinicSeg from "@/components/clinica/ClinicSeg.vue";
import { ClinicReschedules as ReschedulesService } from "@/services/clinica";
import ClinicPickTime from "@/components/clinica/ClinicPickTime.vue";
import ClinicConfirmDialog from "@/components/clinica/ClinicConfirmDialog.vue";

export default {
  name: "ClinicReschedules",
  components: {
    ClinicPageHeader,
    ClinicStateBox,
    ClinicSeg,
    ClinicPickTime,
    ClinicConfirmDialog
  },
  mixins: [clinicCan],
  data() {
    return {
      state: "loading",
      filter: "fila",
      queue: [],
      // Seletor de horário aberto: { mode: 'suggest'|'apply', item }
      picker: null,
      // Reagendamento aguardando confirmação: { item, slot }
      applyConfirm: null,
      applying: false
    };
  },
  created() {
    this.fetch();
  },
  methods: {
    async fetch() {
      this.state = "loading";
      try {
        const { data } = await ReschedulesService.list({
          fail: this.$route.query.state === "error"
        });
        this.queue = data;
        this.state = "ready";
      } catch (e) {
        this.state = "error";
      }
    },
    reload() {
      this.fetch();
    },
    // Formata um slot { datetime: 'YYYY-MM-DDTHH:mm' } → 'DD/MM/AAAA · HH:mm'.
    formatSlot(slot) {
      if (!slot || !slot.datetime) return "—";
      const [date, time] = slot.datetime.split("T");
      const [y, m, d] = date.split("-");
      return `${d}/${m}/${y} · ${(time || "").slice(0, 5)}`;
    },
    // Mesmo slot no formato da frase do modal: 'DD/MM/AAAA às HH:mm'.
    formatSlotLong(slot) {
      return this.formatSlot(slot).replace(" · ", " às ");
    },
    // Abre o seletor de horário real (slots disponíveis do médico).
    suggest(item) {
      this.picker = { mode: "suggest", item };
    },
    // Com sugestão já feita (paciente aceitou), pede confirmação do horário
    // sugerido; sem sugestão, abre o seletor para escolher o novo horário.
    apply(item) {
      if (item.suggested && item.suggested.datetime) {
        this.applyConfirm = { item, slot: item.suggested };
        return;
      }
      this.picker = { mode: "apply", item };
    },
    onPick(datetime) {
      const { mode, item } = this.picker;
      if (mode === "suggest") {
        this.picker = null;
        this.doSuggest(item, { datetime });
        return;
      }
      // Seletor continua aberto por baixo: "Voltar" retorna à escolha feita.
      this.applyConfirm = { item, slot: { datetime } };
    },
    async confirmApply() {
      const { item, slot } = this.applyConfirm;
      this.applying = true;
      const ok = await this.doApply(item, slot);
      this.applying = false;
      if (ok) {
        this.applyConfirm = null;
        this.picker = null;
      }
    },
    async doSuggest(item, slot) {
      try {
        const { data } = await ReschedulesService.suggest(item.id, slot);
        item.status = "sugerido";
        item.suggested = data.suggested;
        this.$toast.success("Horário sugerido ao paciente");
      } catch (e) {
        this.$toast.error("Não foi possível sugerir horário");
      }
    },
    async doApply(item, slot) {
      try {
        await ReschedulesService.apply(item.id, slot);
        this.queue = this.queue.filter(q => q.id !== item.id);
        this.$toast.success("Consulta reagendada");
        return true;
      } catch (e) {
        this.$toast.error("Não foi possível reagendar a consulta");
        return false;
      }
    }
  },
  computed: {
    // Contagens reais derivadas da própria fila (antes eram valores hardcoded).
    pendentes() {
      return this.queue.filter(q => q.status !== "sugerido");
    },
    sugeridos() {
      return this.queue.filter(q => q.status === "sugerido");
    },
    // Abas com contagem real e filtro funcional (antes as abas não filtravam nada).
    filters() {
      return [
        { value: "fila", label: "Na fila", count: this.queue.length },
        {
          value: "pendentes",
          label: "Aguardando",
          count: this.pendentes.length
        },
        { value: "sugeridos", label: "Sugeridos", count: this.sugeridos.length }
      ];
    },
    filteredQueue() {
      if (this.filter === "pendentes") return this.pendentes;
      if (this.filter === "sugeridos") return this.sugeridos;
      return this.queue;
    },
    kpis() {
      return [
        {
          label: "Na fila",
          value: String(this.queue.length),
          foot: "aguardando novo horário"
        },
        {
          label: "Aguardando sugestão",
          value: String(this.pendentes.length),
          foot: "sem horário proposto"
        },
        {
          label: "Já sugeridos",
          value: String(this.sugeridos.length),
          valueClass: "money--pos",
          foot: "aguardando o paciente"
        }
      ];
    }
  }
};
</script>
